import SearchSelect from "../../../GlobalComponents/FormComponents/Dropdowns/SearchSelect";

const LOCKED_HINT = "Save or discard your changes first";

/**
 * The filter bar: year, modules, types, a search box, the pager and any extra
 * controls passed as children.
 *
 * @param {object} props
 * @param {{ value: string, label: string }[]} props.countryOptions
 * @param {string} props.countryCode
 * @param {{ value: string, label: string }[]} props.financialYearOptions
 * @param {string[]} props.financialYearIds at least one; the grid shows every year picked
 * @param {{ value: string, label: string }[]} props.moduleOptions
 * @param {string[]} props.moduleIds empty means every module
 * @param {string} props.search
 * @param {boolean} props.includeInactive also list deactivated KPIs, so they can be switched back on
 * @param {{ value: string, label: string }[]} props.typeOptions
 * @param {string[]} props.activeTypes empty means every type
 * @param {number} props.rowCount rows on this page
 * @param {number} props.total rows matching the filters, across all pages
 * @param {number} props.offset
 * @param {number} props.pageSize
 * @param {boolean} [props.busy] a background refresh is in flight
 * @param {boolean} [props.locked] edits are pending, so context cannot change
 * @param {(offset: number) => void} props.onPage
 * @param {(value: string) => void} props.onCountryChange
 * @param {(values: string[]) => void} props.onFinancialYearsChange
 * @param {(values: string[]) => void} props.onModulesChange
 * @param {(value: string) => void} props.onSearchChange
 * @param {(value: boolean) => void} props.onIncludeInactiveChange
 * @param {(values: string[]) => void} props.onTypesChange
 * @param {React.ReactNode} [props.children]
 */
export default function ContextBar({
  countryOptions,
  countryCode,
  financialYearOptions,
  financialYearIds,
  moduleOptions,
  moduleIds,
  search,
  includeInactive,
  typeOptions,
  activeTypes,
  rowCount,
  total,
  offset,
  pageSize,
  busy,
  locked,
  onPage,
  onCountryChange,
  onFinancialYearsChange,
  onModulesChange,
  onSearchChange,
  onIncludeInactiveChange,
  onTypesChange,
  children,
}) {
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + rowCount;
  const lockedTitle = locked ? LOCKED_HINT : undefined;

  return (
    <div className="kgb-context">
      <SearchSelect
        multiple={false}
        options={countryOptions}
        value={countryCode}
        placeholder="Country"
        ariaLabel="Country"
        disabled={locked}
        title={lockedTitle}
        onChange={onCountryChange}
      />
      <SearchSelect
        options={financialYearOptions}
        value={financialYearIds}
        noun="years"
        placeholder="Financial year"
        ariaLabel="Financial years"
        disabled={locked}
        title={lockedTitle}
        onChange={onFinancialYearsChange}
      />
      <SearchSelect
        options={moduleOptions}
        value={moduleIds}
        allLabel="All modules"
        noun="modules"
        ariaLabel="Modules"
        disabled={locked}
        title={lockedTitle}
        onChange={onModulesChange}
      />
      <SearchSelect
        options={typeOptions}
        value={activeTypes}
        allLabel="All types"
        noun="types"
        ariaLabel="Question types"
        disabled={locked}
        title={lockedTitle}
        onChange={onTypesChange}
      />
      <input
        type="search"
        className="kgb-search"
        value={search}
        placeholder="Search KPIs…"
        aria-label="Search KPIs"
        disabled={locked}
        title={lockedTitle}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <label className="kgb-toggle" title={lockedTitle}>
        <input
          type="checkbox"
          className="kgb-checkbox"
          checked={includeInactive}
          disabled={locked}
          onChange={(e) => onIncludeInactiveChange(e.target.checked)}
        />
        Show inactive
      </label>

      <div className="kgb-context-spacer" />

      <div className="kgb-pager">
        <span className="kgb-rowcount">
          {total === 0 ? "No KPIs" : `${first}–${last} of ${total}`}
          {busy ? " · refreshing" : ""}
        </span>
        {total > pageSize ? (
          <>
            <button
              type="button"
              className="kgb-btn kgb-btn--quiet"
              disabled={offset === 0 || locked}
              title={lockedTitle}
              aria-label="Previous page"
              onClick={() => onPage(offset - pageSize)}
            >
              ‹
            </button>
            <button
              type="button"
              className="kgb-btn kgb-btn--quiet"
              disabled={last >= total || locked}
              title={lockedTitle}
              aria-label="Next page"
              onClick={() => onPage(offset + pageSize)}
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      {children}
    </div>
  );
}
